<script setup lang="ts">
import { nextTick, onMounted, ref, shallowRef, watch } from 'vue'
import type { PaginationQueryParams } from '@/@vxapp/@Enums/PaginationField'
import { MetaLayout } from '@/@vxapp/@Enums/MetaLayout'
import { PaginationField } from '@/@vxapp/@Enums/PaginationField'
import { Subject } from '@/@vxapp/@Enums/PHP/Auth/Ability/Subjects/DataProviders/AbilitySubject'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { cloneReactiveData } from '@/@core/utils/cloneReactiveData'
import { useEventActionStore } from '@/@vxapp/EventActions/useEventActionStore'
import { setPageTitle } from '@/@core/utils/pageMetaUtils'
import EventActionDialog from '@/components/Dialogs/EventActions/EventActionDialog.vue'
import EventActionsDatatable from '@/components/Tables/EventActions/EventActionsDatatable.vue'
import { DialogDisplayType } from '@/enums/dialog-display-type'

type EventActionData = Record<string, unknown>
type DisplayType = (typeof DialogDisplayType)[keyof typeof DialogDisplayType]

definePage({ meta: { subject: Subject.EVENTS_PREVIEW_ALL, layout: MetaLayout.Default } })
setPageTitle('Event Actions')

const log = createLogger('EventActionsListPage')
const route = useRoute()
const router = useRouter()
const eventActionStore = useEventActionStore() as any
const actionsTable = ref<InstanceType<typeof EventActionsDatatable> | null>(null)
const eventActionData = ref<EventActionData | null>(null)
const dialogDisplayType = shallowRef<DisplayType>(DialogDisplayType.CREATE)
const isDialogVisible = shallowRef(false)
const isRouteReady = shallowRef(false)
const searchQuery = ref('')
const resultCount = ref(0)
const defaults: PaginationQueryParams = { ...PaginationField.QueryParams, sort: '-id' }
const limitItems = [10, 25, 50, 100]

const sortItems = [
  { title: 'Newest first', value: '-id' },
  { title: 'Oldest first', value: '+id' },
]

function stringValue(value: unknown) {
  const normalized = Array.isArray(value) ? value[0] : value

  return typeof normalized === 'string' || typeof normalized === 'number'
    ? String(normalized)
    : undefined
}

function positiveInteger(value: unknown, fallback: number) {
  const parsed = Number.parseInt(stringValue(value) ?? '', 10)

  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

function normalize(source: Record<string, unknown>): PaginationQueryParams {
  const requestedLimit = positiveInteger(source.limit, defaults.limit)
  const sort = stringValue(source.sort)
  const search = stringValue(source.search)?.trim()

  return {
    ...defaults,
    page: positiveInteger(source.page, defaults.page),
    limit: limitItems.includes(requestedLimit) ? requestedLimit : defaults.limit,
    sort: sort === '+id' ? '+id' : '-id',
    search: search || undefined,
  }
}

function same(left: PaginationQueryParams, right: PaginationQueryParams) {
  return (
    left.page === right.page &&
    left.limit === right.limit &&
    left.sort === right.sort &&
    left.search === right.search
  )
}

function applyRoute() {
  const current = normalize(eventActionStore.queryParams ?? defaults)
  const incoming = normalize(route.query as Record<string, unknown>)

  searchQuery.value = incoming.search ?? ''
  if (same(current, incoming)) return false
  eventActionStore.queryParams = incoming

  return true
}

function routeQuery(source: Record<string, unknown>) {
  const query = normalize(source)

  return {
    ...(query.page !== defaults.page ? { page: String(query.page) } : {}),
    ...(query.limit !== defaults.limit ? { limit: String(query.limit) } : {}),
    ...(query.sort !== defaults.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
  }
}

async function fetchEventActions() {
  await actionsTable.value?.fetchEventActions()
}

async function updateQuery(patch: Partial<PaginationQueryParams>) {
  const next = normalize({ ...eventActionStore.queryParams, ...patch })

  eventActionStore.queryParams = next

  const query = routeQuery({ ...next })

  if (JSON.stringify(route.query) !== JSON.stringify(query)) await router.replace({ query })
  await nextTick()
  await fetchEventActions()
}

async function handleSearch() {
  await updateQuery({ search: searchQuery.value.trim() || undefined, page: 1 })
}

function handleSearchInput(value: unknown) {
  searchQuery.value = String(value ?? '')
}

async function handleClear() {
  searchQuery.value = ''
  await updateQuery({ search: undefined, page: 1 })
}

async function handleSort(value: unknown) {
  await updateQuery({ sort: String(value), page: 1 })
}

async function handleLimit(value: unknown) {
  await updateQuery({ limit: Number(value), page: 1 })
}

async function handlePage(page: number) {
  await updateQuery({ page })
}

function handleLoaded(count: number) {
  resultCount.value = count
}

async function handleCreateAction() {
  const { template, responseCode } = await eventActionStore.fetchTemplate()

  if (responseCode !== ResponseCode.OK) return
  eventActionData.value = cloneReactiveData(template ?? {})
  dialogDisplayType.value = DialogDisplayType.CREATE
  isDialogVisible.value = true
}

async function openAction(actionGUID: string, displayType: DisplayType) {
  const { action, responseCode } = await eventActionStore.fetch(actionGUID)

  if (responseCode !== ResponseCode.OK) {
    log.error('Failed to load event action', { actionGUID, responseCode })

    return
  }

  eventActionData.value = cloneReactiveData(action ?? {})
  dialogDisplayType.value = displayType
  isDialogVisible.value = true
}

async function handleSave(payload: EventActionData) {
  try {
    const response =
      dialogDisplayType.value === DialogDisplayType.CREATE
        ? await eventActionStore.create(payload, true)
        : await eventActionStore.update(payload, true)

    if (![ResponseCode.OK, ResponseCode.CREATED].includes(response.responseCode)) return
    await fetchEventActions()
  } catch (error) {
    log.error('Failed to save event action', error)
  }
}

async function handleEdit(actionGUID: string) {
  await openAction(actionGUID, DialogDisplayType.EDIT)
}

async function handleView(actionGUID: string) {
  await openAction(actionGUID, DialogDisplayType.VIEW)
}

function handleDialogVisibility(payload: unknown) {
  if (typeof payload === 'boolean') {
    isDialogVisible.value = payload

    return
  }

  if (payload && typeof payload === 'object' && 'visibility' in payload) {
    isDialogVisible.value = Boolean(payload.visibility)
  }
}

eventActionStore.queryParams = { ...defaults }
applyRoute()
isRouteReady.value = true
onMounted(fetchEventActions)

watch(
  () => route.query,
  async () => {
    if (!isRouteReady.value || !applyRoute()) return
    await nextTick()
    await fetchEventActions()
  },
  { deep: true }
)
</script>

<template>
  <main
    class="event-actions-workspace"
    data-testid="event-actions-workspace"
  >
    <section class="event-actions-workspace__hero">
      <span>NO-CODE AUTOMATION / EVENT ACTIONS</span>
      <h1>Turn every event into momentum.</h1>
      <p>
        Connect real event triggers to message, upsell and logging outcomes in one warm workspace.
      </p>
      <div>
        <VBtn
          color="primary"
          @click="handleCreateAction"
          >Create an action</VBtn
        >
        <strong>{{ resultCount }} steps ready</strong>
      </div>
    </section>

    <VCard class="event-actions-workspace__toolbar">
      <VCardText>
        <VForm
          class="event-actions-workspace__search"
          role="search"
          @submit.prevent="handleSearch"
        >
          <!-- vortex-validation-ignore-element: server search is non-validation filter UI -->
          <AppTextField
            :model-value="searchQuery"
            aria-label="Search event actions"
            clearable
            hide-details
            placeholder="Search automation steps"
            vortex-validation-ignore-element
            @click:clear="handleClear"
            @update:model-value="handleSearchInput"
          />
          <VBtn
            color="primary"
            type="button"
            @click="handleSearch"
            >Search</VBtn
          >
        </VForm>
        <!-- vortex-validation-ignore-element: result sorting is non-validation UI -->
        <AppSelect
          :model-value="eventActionStore.queryParams?.sort ?? defaults.sort"
          :items="sortItems"
          aria-label="Sort event actions"
          hide-details
          label="Sort"
          vortex-validation-ignore-element
          @update:model-value="handleSort"
        />
        <!-- vortex-validation-ignore-element: page size is non-validation UI -->
        <AppSelect
          :model-value="eventActionStore.queryParams?.limit ?? defaults.limit"
          :items="limitItems"
          aria-label="Event actions per page"
          hide-details
          label="Per page"
          vortex-validation-ignore-element
          @update:model-value="handleLimit"
        />
      </VCardText>
    </VCard>

    <EventActionsDatatable
      ref="actionsTable"
      @edit="handleEdit"
      @loaded="handleLoaded"
      @page-change="handlePage"
      @view="handleView"
    />
  </main>

  <EventActionDialog
    v-if="eventActionData"
    :action-data="eventActionData"
    :is-dialog-visible="isDialogVisible"
    :title="dialogDisplayType === DialogDisplayType.CREATE ? 'Create Event Action' : 'Event Action'"
    @update="handleSave"
    @update:is-dialog-visible="handleDialogVisibility"
  />
</template>

<style scoped lang="scss">
.event-actions-workspace {
  display: grid;
  min-inline-size: 0;
  gap: 1rem;
  font-family: Inter, system-ui, sans-serif;
}

.event-actions-workspace > * {
  box-sizing: border-box;
  inline-size: 100%;
  max-inline-size: 100%;
  min-inline-size: 0;
}

.event-actions-workspace__hero {
  display: grid;
  min-block-size: 27rem;
  align-content: center;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 0.5rem;
  background: rgb(var(--v-theme-surface-bright));
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  padding: clamp(2rem, 7vw, 5rem);
}

.event-actions-workspace__hero > span {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
}

.event-actions-workspace__hero h1 {
  max-inline-size: 10ch;
  margin: 1rem 0;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: clamp(3.5rem, 8vw, 5.8rem);
  font-weight: 500;
  letter-spacing: -0.04em;
  line-height: 0.9;
}

.event-actions-workspace__hero p {
  max-inline-size: 55ch;
  margin: 0;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 1.1rem;
}

.event-actions-workspace__hero > div {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-block-start: 2rem;
  gap: 1rem;
}

.event-actions-workspace__hero > div strong {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 0.85rem;
}

.event-actions-workspace :deep(.v-btn) {
  min-block-size: 2.75rem;
  border-radius: 0.25rem;
}

.event-actions-workspace__toolbar {
  inline-size: 100%;
  min-inline-size: 0;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 0.5rem;
  background: rgb(var(--v-theme-surface));
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
}

.event-actions-workspace__toolbar :deep(.v-card-text) {
  display: grid;
  align-items: center;
  gap: 0.75rem;
  grid-template-columns: minmax(16rem, 1fr) minmax(10rem, 0.3fr) minmax(8rem, 0.2fr);
  padding: 0.75rem;
}

.event-actions-workspace__search {
  display: grid;
  min-inline-size: 0;
  gap: 0.5rem;
  grid-template-columns: minmax(0, 1fr) auto;
}

.event-actions-workspace__toolbar :deep(.v-card-text > *),
.event-actions-workspace__toolbar :deep(.v-input) {
  inline-size: 100%;
  min-inline-size: 0;
}

@media (max-width: 799px) {
  .event-actions-workspace__toolbar :deep(.v-card-text) {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 420px) {
  .event-actions-workspace__hero {
    padding: 1.5rem;
  }

  .event-actions-workspace__hero h1 {
    overflow-wrap: anywhere;
    font-size: clamp(2.25rem, 11vw, 3rem);
  }

  .event-actions-workspace__search {
    grid-template-columns: 1fr;
  }
}
</style>
